// @ts-strict-ignore
import type { SearchFilterCategories } from "@shared/components/CatalogSearch/CatalogSearch";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";

const useProductSearchVariables = (skipSort?: boolean) => {
  const {
    activeIngredients,
    excludedIngredients,
    activeBrands,
    activeSupplementTypes,
    activeConditions,
    activeFilters,
    activeCategories,
    activeTaxonomies,
    activeOptimizedTaxonomies,
    activeEqpLevels,
    searchQuery,
    sortDirection,
    sortField,
    isOnlyShowFavorites,
    onlyIngredient,
    isOnlyShowInStock,
    isOnlyShowEmersonNew,
    isFsTested,
    typeaheadSelectionId,
    activeRelevanceFilters,
  } = useCatalogSearch();

  const getActiveFilterIds = (category: SearchFilterCategories) =>
    activeFilters
      .filter(filter => filter.category === category)
      .map(filteredFilter => filteredFilter.id);

  const brandIds = activeBrands.map(brand => brand.id);

  const supplementTypeIds = activeSupplementTypes.map(type => type.id);

  const conditionIds = activeConditions?.map(condition => condition.id);

  const categoryIds = activeCategories?.map(category => category.id);

  const taxonomyIds = activeTaxonomies?.map(taxonomy => taxonomy.id);

  const optimizedTaxonomyIds = activeOptimizedTaxonomies
    ?.filter(taxonomy => taxonomy?.type !== "search")
    ?.map(taxonomy => taxonomy.id);

  const ingredients = activeIngredients
    ? activeIngredients.map(ingredient => ingredient.searchAttributes)
    : null;

  const excludedIngredientsForQuery = excludedIngredients
    ? excludedIngredients.map(ingredient => ingredient.searchAttributes)
    : null;

  const allergenIds = getActiveFilterIds("Allergens");
  const thirdPartyCertificationIds = getActiveFilterIds("Certifications");
  const populationIds = getActiveFilterIds("Demographic");
  const baseDosageFormatIds = getActiveFilterIds("Form");
  const eqpLevels = activeEqpLevels;

  const onlyWholesaleHistory =
    getActiveFilterIds("More")?.filter(id => id === "previouslyOrdered")?.length > 0;

  const showOnlySaleItems = getActiveFilterIds("More")?.filter(id => id === "onSale")?.length > 0;

  const sort =
    !skipSort && sortDirection && sortField ? [{ direction: sortDirection, field: sortField }] : [];

  const initialFilterSelectionId = (): string => {
    const nonBrandFilter = activeRelevanceFilters?.find(f => f.category !== "Brand");
    if (nonBrandFilter) {
      return nonBrandFilter.id;
    }

    const brandFilter = activeRelevanceFilters?.find(f => f.category === "Brand");
    if (brandFilter) {
      return brandFilter.id;
    }

    return "";
  };

  const getRelevanceFilterQuery = (): string => {
    const filter = activeRelevanceFilters?.find(f => f.id === initialFilterSelectionId());
    if (filter) {
      return filter.query;
    }

    return "";
  };

  return {
    query: searchQuery,
    typeaheadSelectionId,
    initialFilterSelectionQuery: getRelevanceFilterQuery(),
    initialFilterSelectionId: initialFilterSelectionId(),
    sort,
    filters: {
      onlyShowFavorites: isOnlyShowFavorites,
      wholesaleDiscounted: showOnlySaleItems,
      brandIds,
      supplementTypeIds,
      conditionIds,
      categoryIds,
      taxonomyIds,
      optimizedTaxonomyIds,
      ingredients,
      excludedIngredients: excludedIngredientsForQuery,
      allergenIds,
      thirdPartyCertificationIds,
      populationIds,
      baseDosageFormatIds,
      inStockOnly: isOnlyShowInStock,
      eeNew: isOnlyShowEmersonNew,
      onlyIngredient,
      onlyWholesaleHistory,
      eqpLevels,
      isFsTested,
    },
  };
};

export { useProductSearchVariables };
